import React, { createContext, useLayoutEffect, useMemo, useRef, type RefObject } from 'react'
import { cn } from './cn'
import { usePopoverDismiss } from './use-popover-dismiss'
import {
  usePopoverPlacement,
  type PopoverAlign,
  type PopoverAnchor,
} from './use-popover-placement'

/**
 * The panel every menu entry opens (`Menu`, `MenuButton`, `ContextMenu`): placement,
 * the close contract, roving focus across items. Not exported from the kit.
 *
 * It renders in place with `position: fixed`, like Dropdown and Picker, and is not
 * portalled: a menu may open inside a `KiaoModal` `<dialog>`, and a portal to
 * <body> would land under the top layer and turn inert.
 */

export type FocusTarget = 'first' | 'last'

export interface MenuContextValue {
  wide: boolean
  /** Close, then hand focus back to whatever opened the menu. */
  closeAndRefocus: () => void
}

export const MenuContext = createContext<MenuContextValue>({
  wide: false,
  closeAndRefocus: () => {},
})

const ITEM_SELECTOR = '[role^="menuitem"]:not([disabled])'

export interface MenuPanelProps {
  id: string
  label: string
  wide: boolean
  anchor: RefObject<PopoverAnchor | null>
  align: PopoverAlign
  focusTarget: FocusTarget
  /** Elements whose pointerdown does not count as "outside" (the trigger). */
  insideRefs: ReadonlyArray<RefObject<HTMLElement | null>>
  onClose: (refocus: boolean) => void
  children: React.ReactNode
}

export function MenuPanel({
  id,
  label,
  wide,
  anchor,
  align,
  focusTarget,
  insideRefs,
  onClose,
  children,
}: MenuPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [position] = usePopoverPlacement(
    true,
    { trigger: anchor, panel: panelRef, scrollArea: scrollAreaRef, content: contentRef },
    [],
    { align, sizeToPanel: true }
  )

  usePopoverDismiss(true, {
    containers: [panelRef, ...insideRefs],
    onPointerDownOutside: () => onClose(false),
    onEscapeKey: () => onClose(true),
  })

  const items = () =>
    Array.from(panelRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? [])

  // Focus waits for the first measurement: until then the panel is
  // `visibility: hidden`, and a hidden element cannot take focus.
  const placed = position !== null
  useLayoutEffect(() => {
    if (!placed) return
    const all = items()
    ;(focusTarget === 'last' ? all[all.length - 1] : all[0])?.focus()
  }, [placed, focusTarget])

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      onClose(false)
      return
    }
    const all = items()
    if (all.length === 0) return
    const from = all.indexOf(document.activeElement as HTMLElement)
    const last = all.length - 1
    let next: number | null = null
    if (event.key === 'ArrowDown') next = from >= last ? 0 : from + 1
    else if (event.key === 'ArrowUp') next = from <= 0 ? last : from - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === null) return
    event.preventDefault()
    all[next].focus()
  }

  const context = useMemo(() => ({ wide, closeAndRefocus: () => onClose(true) }), [wide, onClose])

  return (
    <MenuContext.Provider value={context}>
      <div
        ref={panelRef}
        id={id}
        role="menu"
        aria-label={label}
        aria-orientation="vertical"
        onKeyDown={onKeyDown}
        className={cn(
          'fixed z-[70] flex flex-col overflow-hidden bg-background text-foreground shadow-[var(--shadow-pop)]',
          wide
            ? 'w-[var(--measure-menu-wide)] rounded-[var(--radius-lg)] p-1.5'
            : 'w-[var(--measure-menu)] rounded-[var(--radius-menu)] py-1'
        )}
        style={
          position
            ? {
                left: position.left,
                top: position.top,
                bottom: position.bottom,
                maxHeight: position.maxHeight,
              }
            : { visibility: 'hidden' }
        }
      >
        <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-y-auto">
          <div ref={contentRef} className="flex flex-col">
            {children}
          </div>
        </div>
      </div>
    </MenuContext.Provider>
  )
}
